import { BriefSection, PartnershipTier, TierType } from "@/types/types";

const TIER_TYPE_LABEL: Record<TierType, string> = {
  paid: "Paid",
  affiliate: "Affiliate",
  combined: "Paid + Affiliate",
};

const PAID_TYPES = new Set(["max_budget", "max_cpm", "custom_formula"]);
const AFFILIATE_TYPES = new Set(["cpa", "revenue_share", "other", "performance_cpm"]);

function inferTierType(tier: PartnershipTier): TierType {
  if (tier.tier_type) return tier.tier_type;
  const types = (tier.components || []).map((c) => c.type);
  const hasPaid = types.some((t) => PAID_TYPES.has(t));
  const hasAffiliate = types.some((t) => AFFILIATE_TYPES.has(t));
  if (hasPaid && hasAffiliate) return "combined";
  if (hasAffiliate) return "affiliate";
  return "paid";
}

export const ACTIVATION_MESSAGE_AI_PROMPT =
  "You are crafting the activation message for creators. Start with the user's exact input here → {{current_content}}. Polish and clarify it for creators in 1–2 sentences. If key details are missing, enrich only with verified facts from {{website_url}} or {{faq}}. Do not invent information, and never override clear user wording.";

export const REJECTION_MESSAGE_SECTION: BriefSection = {
  id: "rejection_message",
  title: "Rejection Message",
  description:
    "This message will be sent to creators when they are rejected from the campaign. If left empty, Amy will generate a polite rejection message automatically.",
  placeholder: "Enter custom rejection message here...",
  aiPrompt:
    "You are crafting the rejection message for creators. Start with the user's exact input here → {{current_content}}. Polish and clarify it for creators in 1–2 sentences. Keep a polite, professional tone that thanks the creator and leaves the door open for future opportunities. Do not invent information, and never override clear user wording.",
  required: false,
};

export function buildActivationMessageSection(
  tier: PartnershipTier
): BriefSection {
  const tierType = inferTierType(tier);
  const typeLabel = TIER_TYPE_LABEL[tierType];
  return {
    id: `activation_message_tier_${tier.order}`,
    title: `Tier ${tier.order}: Activation Message - ${typeLabel}`,
    description: `This message is sent to creators who close in Tier ${tier.order} (${typeLabel}). Include tier-specific onboarding details, links, and next steps for this compensation structure.`,
    placeholder: `Enter activation message for Tier ${tier.order} (${typeLabel})...`,
    aiPrompt: ACTIVATION_MESSAGE_AI_PROMPT,
    required: tier.order === 1,
  };
}

export function buildMessageSections(
  tiers: PartnershipTier[] = []
): BriefSection[] {
  const sortedTiers = [...tiers].sort((a, b) => a.order - b.order);
  const activationSections = sortedTiers.map(buildActivationMessageSection);
  return [...activationSections, REJECTION_MESSAGE_SECTION];
}
